import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import { getAllAuthors } from "@/api/content";
import type { AuthorProfile } from "@/types/content";

export default function AuthorsPage({ initialData }: { initialData?: AuthorProfile[] }) {
  const [authors, setAuthors] = useState<AuthorProfile[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);

  useEffect(() => {
    getAllAuthors().then((data) => {
      setAuthors(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="max-w-6xl mx-auto">
        <SEO
          meta={{
            title: "作者專區｜美好時光指南",
            description: "認識美好時光指南的神秘客與編輯團隊，瀏覽他們的專業評測文章。",
            canonical: canonical("/authors"),
            keywords: ["作者專區", "神秘客", "編輯團隊", "評測文章", "美好時光指南"],
            structuredData: collectionPageSchema("作者專區｜美好時光指南", "認識美好時光指南的神秘客與編輯團隊。", "/authors"),
          }}
        />

        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
          作者專區
        </h1>
        <p className="text-slate-500 mb-10 max-w-2xl">
          認識我們的神秘客與編輯團隊，每位作者都親自體驗、真實評測，帶給你最可靠的活動推薦
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {authors.map((author) => (
            <a
              key={author.id}
              href={`/authors/${author.slug}`}
              className="group block bg-white rounded-xl p-6 md:p-8 border border-slate-100 hover:border-primary/20 transition-all text-center"
            >
              <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto mb-4">
                <img
                  loading="lazy"
                  src={author.avatar}
                  alt={author.name}
                  className="w-full h-full object-cover rounded-full border-2 border-slate-100 group-hover:border-primary/30 transition-colors"
                />
              </div>
              <h2 className="text-lg font-semibold text-slate-800 mb-1 group-hover:text-primary transition-colors">
                {author.name}
              </h2>
              <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                {author.bio}
              </p>
              <div className="flex items-center justify-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <i className="ri-article-line" />
                  {author.articleCount} 篇文章
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-line" />
                  {author.reviewCount} 則評價
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}